/**
 * Multi-currency banking rails for Conta internacional via MobileService:
 * GET /v1/ib/accounts/currencies → BaaS GET /v1/accounts/{activeId}/currencies
 * (OpenAccount items with banking scheme br_compe | us_comp | international).
 */

import { fetchAxios } from "../API/fetchAxios";
import { mapBaasBankingToUi } from "./baasAccountsListApi";
import { CURRENCIES, DEPOSIT_METHODS, TRANSFER_METHODS } from "../Pages/Private/ContaInternacional/constants";

const metaByCode = CURRENCIES.reduce((map, currency) => {
  map[currency.code.toUpperCase()] = currency;
  return map;
}, {});

const extractItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

/** Map BaaS banking scheme + currency → Conta internacional rail ids. */
export const resolveRailsForCurrency = (currencyCode, scheme) => {
  const code = String(currencyCode || "").toUpperCase();
  const normalizedScheme = String(scheme || "").toLowerCase();

  if (normalizedScheme === "br_compe" || code === "BRL") return ["pix"];
  if (normalizedScheme === "us_comp" || code === "USD") return ["wire", "ach", "swift"];
  if (code === "EUR") return ["sepa", "swift"];
  return ["swift"];
};

/** Map partner banking fields onto Conta internacional deposit/dados field keys. */
export const mapBankingToRailDetails = (bankingUi = {}) => {
  const details = {};
  if (bankingUi.bankName || bankingUi.bankLabel) {
    details.bankName = bankingUi.bankName || bankingUi.bankLabel;
  }
  if (bankingUi.accountNumber) details.accountNumber = bankingUi.accountNumber;
  if (bankingUi.branch) details.branch = bankingUi.branch;
  if (bankingUi.routingNumber) details.routingNumber = bankingUi.routingNumber;
  if (bankingUi.bic) details.swiftBic = bankingUi.bic;
  if (bankingUi.ispb) details.pixKey = bankingUi.ispb;

  return Object.keys(details).length ? details : null;
};

export const mapCurrencyItemToUi = (item) => {
  const code = String(item?.currency_code || item?.currencyCode || "").toUpperCase();
  if (!code) return null;

  const id = code.toLowerCase();
  const meta = metaByCode[code];
  const banking = mapBaasBankingToUi(item?.banking ?? {});
  const railIds = resolveRailsForCurrency(code, banking.scheme);
  const railDetails = mapBankingToRailDetails(banking);
  const detailsByRail = {};
  railIds.forEach((railId) => {
    if (railDetails) detailsByRail[railId] = { ...railDetails };
  });

  return {
    id,
    code,
    label: meta?.label || code,
    countryCode: item?.country_code || item?.countryCode || null,
    accountType: item?.account_type || item?.accountType || null,
    verificationStatus: item?.verification_status || item?.verificationStatus || null,
    banking,
    railIds,
    rails: railIds.map((railId) => TRANSFER_METHODS[railId] || DEPOSIT_METHODS[railId]).filter(Boolean),
    detailsByRail,
    raw: item,
  };
};

/** Live currency openings + banking for the active IB account. */
export const fetchIbAccountCurrencies = async () => {
  const response = await fetchAxios({
    verb: "GET",
    context: "accountCurrencies",
    responseType: "",
    cancelToken: undefined,
  });
  return extractItems(response?.data ?? response)
    .map(mapCurrencyItemToUi)
    .filter(Boolean)
    .sort((a, b) => {
      if (a.code === "BRL") return 1;
      if (b.code === "BRL") return -1;
      return a.code.localeCompare(b.code);
    });
};

export const getCurrencyRails = (currencies, currencyId) => {
  const id = String(currencyId || "").toLowerCase();
  const match = (currencies || []).find((row) => row.id === id);
  return match?.rails ?? [];
};

export const getCurrencyBankingDetails = (currencies, currencyId, railId) => {
  const id = String(currencyId || "").toLowerCase();
  const match = (currencies || []).find((row) => row.id === id);
  return match?.detailsByRail?.[railId] ?? null;
};
